import { isCapacityRead } from "@/queries/capacity";
import { rereadOlderThan } from "@/queries/reread";
import { scheduleHintArrived } from "@/queries/schedule-invalidation";
import type { QueryScope } from "@/queries/scope";
import type { THintTarget, THintTargets } from "./types";

/** When the latest `capacity:list` hint arrived, per workspace and principal. */
const hintedAt = new Map<string, number>();
const scopeId = (scope: QueryScope) => `${scope.tenant}|${scope.principal}`;

/**
 * Capacity's hint row (NG-2812). The backend publishes `capacity:list` once a
 * background rebuild or rollup refresh has rewritten the rows the grid reads,
 * which lands after the write that caused it has returned. So the writer's own
 * re-read can miss it, and a teammate's open grid hears of it only here.
 *
 * Unlike `observedRead`, it re-reads only what was read before the hint
 * arrived, and cancels nothing: a read started since (a pan, or the writer's
 * own re-read) already sees the rebuilt rows, and asking again would cancel it
 * and cost a second request (the PR-7210 box test, `reread.ts`). A tab waiting
 * on the hint for its own write (`awaitScheduleHint`) re-reads as it arrives,
 * without the jitter; its jittered re-read then finds nothing older to ask for.
 */
const capacityList: THintTarget = {
  mark: (_root, _slug, { client, scope }) => {
    const at = Date.now();
    hintedAt.set(scopeId(scope), at);
    void client.invalidateQueries({ predicate: isCapacityRead(scope), refetchType: "none" });
    // The rebuild this tab's own write is waiting on: re-read now, not after the jitter.
    scheduleHintArrived(client, at);
  },
  reread: (_root, _slug, { client, scope }) => {
    const at = hintedAt.get(scopeId(scope));
    if (at === undefined) return;
    void rereadOlderThan(client, isCapacityRead(scope), at);
  },
};

export const CAPACITY_HINTS: THintTargets = {
  "capacity:list": capacityList,
};
